import copy from 'copy-to-clipboard'

import { CommandBarItem } from '@sketch/modules-common'
import {
  ElementType,
  SketchElement,
  SketchLayerCSSProperties,
} from '../../inspector'
import { generateLayerCSS } from '../FrameView/components/FrameDetailInspector/useGenerateLayerCSS'

import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'
import { ReactComponent as OpenFrameIcon } from '@sketch/icons/frame-17'
import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'

interface CreateInspectCommandsProps {
  navigateToFrame: (frameUUID: string, source: 'CTA') => void
  openExportModal: (id: bigint, name: string) => void
  selectedElement: SketchElement | null
  showToast: (message: string) => void
  sketchSceneRootElement: SketchElement | null
}

export const createInspectCommands = ({
  navigateToFrame,
  openExportModal,
  selectedElement,
  showToast,
  sketchSceneRootElement,
}: CreateInspectCommandsProps): CommandBarItem[] => {
  if (!selectedElement) {
    return []
  }

  const nearestOpenableFrame = selectedElement.nearestOpenableFrame
  const isOpenableFrameCurrentlyOpen =
    nearestOpenableFrame &&
    nearestOpenableFrame.targetUUID === sketchSceneRootElement?.elementUUID

  const openFrameCommand: CommandBarItem | null =
    nearestOpenableFrame?.type === ElementType.Artboard &&
    !isOpenableFrameCurrentlyOpen
      ? {
          id: `inspect.open-frame.${selectedElement.identifier}`,
          analyticsId: 'inspect.open-frame',
          kind: 'command',
          title: nearestOpenableFrame.isSelf
            ? 'Open Frame'
            : 'Open Top-Level Frame',
          subtitle: selectedElement.name,
          section: 'Inspect',
          keywords: ['inspect', 'open', 'frame', 'navigate'],
          icon: OpenFrameIcon,
          priority: 25,
          run: () => navigateToFrame(nearestOpenableFrame.targetUUID, 'CTA'),
        }
      : null

  return [
    ...(openFrameCommand ? [openFrameCommand] : []),
    {
      id: `inspect.copy-css.${selectedElement.identifier}`,
      analyticsId: 'inspect.copy-css',
      kind: 'command',
      title: 'Copy CSS',
      subtitle: selectedElement.name,
      section: 'Inspect',
      keywords: ['inspect', 'export', 'css'],
      icon: CopyCSSIcon,
      priority: 25,
      run: () => {
        const layerCSS = generateLayerCSS(
          selectedElement as SketchLayerCSSProperties
        )

        if (!layerCSS.css.length) return
        copy(layerCSS.toString())
        showToast('CSS copied')
      },
    },
    {
      id: `inspect.export.${selectedElement.identifier}`,
      analyticsId: 'inspect.export',
      kind: 'command',
      title: 'Export as…',
      subtitle: selectedElement.name,
      section: 'Inspect',
      keywords: ['inspect', 'export', 'download', 'asset'],
      icon: ExportIcon,
      priority: 25,
      run: () =>
        openExportModal(selectedElement.identifier, selectedElement.name),
    },
  ]
}
